import React, { FC, useState } from 'react';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { WaterAuthority } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';

interface WaterAuthorityPdfUploadProps {
  waterAuthority: WaterAuthority;
  onUploadSuccess?: () => void;
}

const WaterAuthorityPdfUpload: FC<WaterAuthorityPdfUploadProps> = (props) => {
  const { waterAuthority, onUploadSuccess } = props;

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) return;

    const file = fileList[0];

    if (file.type !== 'application/pdf') {
      setErrorMessage('Please upload a PDF file');
      event.target.value = '';
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('pdf_template', file, file.name);
      formData.append('water_authority_id', waterAuthority.id);

      await axios.post(`/backflows/water_authority_pdf_templates`, formData);

      setSuccessMessage('PDF template uploaded successfully!');
      setErrorMessage('');

      // Call the success callback to refresh field designations
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err: any) {
      console.error('Error uploading PDF:', err);
      setErrorMessage(err?.response?.data?.error || 'Failed to upload PDF template. Please try again.');
      setSuccessMessage('');
    } finally {
      setLoading(false);
      // Allow re-uploading the same file name again
      event.target.value = '';
    }
  };

  return (
    <LoadingWrapper isLoading={loading} message="Uploading...">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
          {successMessage && (
            <Typography variant="body2" sx={{ color: 'success.main', mt: 1 }}>
              {successMessage}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          <input
            type="file"
            id="pdf-upload-input"
            accept=".pdf,application/pdf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            disabled={loading || !waterAuthority?.id}
          />
          <Button
            variant="contained"
            component="label"
            htmlFor="pdf-upload-input"
            disabled={loading || !waterAuthority?.id}
            size="large"
          >
            Upload PDF Template
          </Button>
        </Grid>
        {waterAuthority?.pdf_template_url && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Current template: </strong>
              <a
                href={waterAuthority.pdf_template_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
              >
                View PDF →
              </a>
            </Typography>
          </Grid>
        )}
        {waterAuthority?.field_name_demo_url && (
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mt: 2 }}>
              <strong>Demo with Field Names: </strong>
              <a
                href={waterAuthority.field_name_demo_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
              >
                View Demo →
              </a>
            </Typography>
          </Grid>
        )}
      </Grid>
    </LoadingWrapper>
  );
};

export default WaterAuthorityPdfUpload;
